import { useRef } from 'react';

import { Button, Drawer, DrawerTrigger, Group, Stack } from '@ekhodealer/ekho-common/rev';

import type { ReactNode } from 'react';

import { PLACEHOLDER } from '@common/lib/strings';

import './FiltersDrawer.scss';

interface FiltersDrawerProps {
	/** The trigger element that opens the drawer */
	trigger: ReactNode;
	/** Optional applied filters section at the top */
	appliedFiltersSection?: ReactNode;
	/** The filter sections content (includes all filter components) */
	filterSections: ReactNode;
	/** Whether any filters are currently applied */
	hasAppliedFilters: boolean;
	/** Function to clear all applied filters */
	clearAllFilters: () => void;
	/** The count of results from the query */
	queryCount: number;
	/** Whether the query is currently loading */
	isLoading: boolean;
	/** Optional custom CSS class for feature-specific styling */
	className?: string;
}

/**
 * FiltersDrawer - A shared, reusable drawer component for filtering data
 *
 * This component provides a consistent structure for filter drawers across features
 * while allowing each feature to inject their own custom filter sections.
 *
 * Uses Drawer.Content + Drawer.Footer slots so the footer stays pinned at the bottom
 * without overlapping scrollable content.
 */
export const FiltersDrawer = ({
	trigger,
	appliedFiltersSection,
	filterSections,
	hasAppliedFilters,
	clearAllFilters,
	queryCount,
	isLoading,
	className,
}: FiltersDrawerProps) => {
	const closeDrawerRef = useRef<(() => void) | null>(null);

	return (
		<DrawerTrigger>
			{trigger}
			<Drawer placement='right' className={className}>
				<Drawer.Header>Filters</Drawer.Header>
				<Drawer.Content>
					{(closeDrawer: () => void) => {
						closeDrawerRef.current = closeDrawer;
						return (
							<Stack padding={{ x: 24, top: 12 }}>
								<Stack itemsSpacing='24'>
									{appliedFiltersSection}
									{filterSections}
								</Stack>
							</Stack>
						);
					}}
				</Drawer.Content>
				<Drawer.Footer>
					<Group itemsSpacing='8' itemsAlignX='stretch' noWrap>
						{hasAppliedFilters && (
							<Group.Item grow={1}>
								<Button
									variant='secondary'
									size='large'
									onPress={() => clearAllFilters()}>
									Clear all
								</Button>
							</Group.Item>
						)}
						<Group.Item grow={hasAppliedFilters ? 3 : 1}>
							<Button
								size='large'
								isFullWidth
								onPress={() => closeDrawerRef.current?.()}>
								View results {isLoading ? PLACEHOLDER : `(${queryCount})`}
							</Button>
						</Group.Item>
					</Group>
				</Drawer.Footer>
			</Drawer>
		</DrawerTrigger>
	);
};
